import { useState, useEffect } from 'react';
import { Brain, TrendingUp, CheckCircle, XCircle, Clock, DollarSign, Award, BarChart3, AlertCircle, MapPin, Power } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { createDispatch } from '../../services/dispatchService';
import { LoadDetailView } from './LoadDetailView';

interface AIDecision {
  id: string;
  timestamp: string;
  loadNumber: string;
  dispatchNumber?: string;
  recommendedDriver: string;
  approved: boolean;
  reasoning: string;
  actualRevenue?: number;
  estimatedRevenue: number;
  deliveryOnTime?: boolean;
  alternativesCount: number;
}

interface Driver {
  id: string;
  name: string;
  truckNumber: string;
  distanceToPickup: number;
  weeklyRevenue: number;
  availableHours: number;
  weeklyMiles: number;
}

interface PendingDecision {
  id: string;
  timestamp: string;
  loadNumber: string;
  pickup: string;
  delivery: string;
  pickupTime: string;
  deliveryTime: string;
  rate: number;
  totalMiles: number;
  recommendedDriver: Driver;
  reasoning: string;
  alternativeDrivers: Array<{
    driver: Driver;
    score: number;
    reason: string;
  }>;
  urgency: 'high' | 'medium' | 'low';
}

export function AIAnalyticsView() {
  const toast = useToast();
  const [aiEnabled, setAiEnabled] = useState(
    localStorage.getItem('ai_dispatch_enabled') === 'true'
  );
  const [selectedLoad, setSelectedLoad] = useState<string | null>(null);
  const [selectedDispatch, setSelectedDispatch] = useState<string | null>(null);

  // Check AI status on mount and listen for changes
  useEffect(() => {
    const checkAIStatus = () => {
      const enabled = localStorage.getItem('ai_dispatch_enabled') === 'true';
      setAiEnabled(enabled);
    };
    
    const handleAIToggleChange = (event: CustomEvent) => {
      const enabled = event.detail?.enabled ?? localStorage.getItem('ai_dispatch_enabled') === 'true';
      setAiEnabled(enabled);
    };
    
    // Listen for AI toggle changes from header
    window.addEventListener('aiToggleChanged', handleAIToggleChange as EventListener);
    window.addEventListener('storage', checkAIStatus);
    
    return () => {
      window.removeEventListener('aiToggleChanged', handleAIToggleChange as EventListener);
      window.removeEventListener('storage', checkAIStatus);
    };
  }, []);

  const handleEnableAI = () => {
    localStorage.setItem('ai_dispatch_enabled', 'true');
    setAiEnabled(true);
    
    // Log activity
    const activity = {
      id: Math.random().toString(36).substring(7),
      timestamp: new Date().toISOString(),
      user: 'Current User', // In production, get from auth context
      action: 'Enabled AI Dispatch',
      details: 'User enabled AI-powered load assignment from AI Dispatch Analytics page',
      type: 'system_change',
    };
    
    const activities = JSON.parse(localStorage.getItem('activities') || '[]');
    activities.unshift(activity);
    localStorage.setItem('activities', JSON.stringify(activities));
    
    toast.success('AI Dispatch enabled! The system will now recommend drivers for new loads.');
    
    // Trigger custom event for header to update
    window.dispatchEvent(new CustomEvent('aiToggleChanged', { detail: { enabled: true } }));
  };
  
  // Mock data - in production, this would come from database
  const [pendingDecisions, setPendingDecisions] = useState<PendingDecision[]>([
    {
      id: 'p1',
      timestamp: '2024-11-28 15:45',
      loadNumber: 'L-27430',
      pickup: 'Chicago, IL',
      delivery: 'Dallas, TX',
      pickupTime: '2024-11-29 08:00',
      deliveryTime: '2024-11-30 14:00',
      rate: 2850,
      totalMiles: 967,
      recommendedDriver: {
        id: 'd1',
        name: 'Mike Johnson',
        truckNumber: 'T-154',
        distanceToPickup: 35,
        weeklyRevenue: 2100,
        availableHours: 13.5,
        weeklyMiles: 1850,
      },
      reasoning: 'Mike is 35 miles from pickup, has 13.5 available ELD hours, and has the lowest weekly revenue this week ($2,100). He needs workload balance.',
      alternativeDrivers: [
        {
          driver: {
            id: 'd2',
            name: 'John Doe',
            truckNumber: 'T-201',
            distanceToPickup: 42,
            weeklyRevenue: 2650,
            availableHours: 12.0,
            weeklyMiles: 2100,
          },
          score: 85,
          reason: 'Slightly farther but has good availability and balanced workload',
        },
        {
          driver: {
            id: 'd3',
            name: 'Jane Smith',
            truckNumber: 'T-089',
            distanceToPickup: 28,
            weeklyRevenue: 3200,
            availableHours: 11.5,
            weeklyMiles: 2450,
          },
          score: 78,
          reason: 'Closest to pickup but higher weekly revenue',
        },
        {
          driver: {
            id: 'd4',
            name: 'Tom Wilson',
            truckNumber: 'T-312',
            distanceToPickup: 65,
            weeklyRevenue: 1950,
            availableHours: 14.0,
            weeklyMiles: 1600,
          },
          score: 72,
          reason: 'Lowest revenue but farther from pickup location',
        },
      ],
      urgency: 'high',
    },
    {
      id: 'p2',
      timestamp: '2024-11-28 14:20',
      loadNumber: 'L-27428',
      pickup: 'Los Angeles, CA',
      delivery: 'Phoenix, AZ',
      pickupTime: '2024-11-29 14:00',
      deliveryTime: '2024-11-29 21:00',
      rate: 1450,
      totalMiles: 373,
      recommendedDriver: {
        id: 'd5',
        name: 'Sarah Williams',
        truckNumber: 'T-445',
        distanceToPickup: 55,
        weeklyRevenue: 2850,
        availableHours: 11.0,
        weeklyMiles: 2200,
      },
      reasoning: 'Sarah will arrive in the area in 3 hours after current delivery. Good revenue balance and sufficient hours.',
      alternativeDrivers: [
        {
          driver: {
            id: 'd6',
            name: 'Bob Martinez',
            truckNumber: 'T-567',
            distanceToPickup: 48,
            weeklyRevenue: 2950,
            availableHours: 12.5,
            weeklyMiles: 2300,
          },
          score: 82,
          reason: 'Closer and more available hours',
        },
      ],
      urgency: 'medium',
    },
  ]);

  const [decisions, setDecisions] = useState<AIDecision[]>([
    {
      id: '1',
      timestamp: '2024-11-28 14:30',
      loadNumber: 'L-27425',
      dispatchNumber: 'DIS-789456',
      recommendedDriver: 'John Doe',
      approved: true,
      reasoning: 'Closest to pickup with good availability',
      actualRevenue: 2850,
      estimatedRevenue: 2800,
      deliveryOnTime: true,
      alternativesCount: 3,
    },
    {
      id: '2',
      timestamp: '2024-11-28 10:15',
      loadNumber: 'L-27420',
      dispatchNumber: 'DIS-789123',
      recommendedDriver: 'Jane Smith',
      approved: true,
      reasoning: 'Best revenue balance',
      actualRevenue: 3200,
      estimatedRevenue: 3100,
      deliveryOnTime: true,
      alternativesCount: 2,
    },
    {
      id: '3',
      timestamp: '2024-11-27 16:45',
      loadNumber: 'L-27415',
      recommendedDriver: 'Mike Johnson',
      approved: false,
      reasoning: 'Lowest weekly miles',
      estimatedRevenue: 2600,
      deliveryOnTime: false,
      alternativesCount: 4,
    },
    {
      id: '4',
      timestamp: '2024-11-27 09:20',
      loadNumber: 'L-27410',
      dispatchNumber: 'DIS-788901',
      recommendedDriver: 'Sarah Williams',
      approved: true,
      reasoning: 'Future location consideration',
      actualRevenue: 2400,
      estimatedRevenue: 2500,
      deliveryOnTime: false,
      alternativesCount: 3,
    },
  ]);

  const stats = {
    totalDecisions: decisions.length,
    approved: decisions.filter(d => d.approved).length,
    rejected: decisions.filter(d => !d.approved).length,
    approvalRate: Math.round((decisions.filter(d => d.approved).length / decisions.length) * 100),
    onTimeDeliveries: decisions.filter(d => d.deliveryOnTime === true).length,
    averageRevenue: Math.round(
      decisions.filter(d => d.actualRevenue).reduce((sum, d) => sum + (d.actualRevenue || 0), 0) /
      decisions.filter(d => d.actualRevenue).length
    ),
    revenueAccuracy: Math.round(
      (decisions.filter(d => d.actualRevenue).reduce((sum, d) => {
        const accuracy = ((d.actualRevenue || 0) / d.estimatedRevenue) * 100;
        return sum + accuracy;
      }, 0) / decisions.filter(d => d.actualRevenue).length)
    ),
  };

  const [selectedDrivers, setSelectedDrivers] = useState<Record<string, string>>({});
  const [expandedOptions, setExpandedOptions] = useState<Record<string, boolean>>({});

  const handleApprove = async (pendingId: string, driverId?: string) => {
    const pending = pendingDecisions.find(d => d.id === pendingId);
    if (!pending) return;
    
    const selectedDriver = driverId 
      ? pending.alternativeDrivers.find(alt => alt.driver.id === driverId)?.driver || pending.recommendedDriver
      : pending.recommendedDriver;
    
    try {
      // Create dispatch
      const dispatch = await createDispatch({
        loadNumber: pending.loadNumber,
        driverId: selectedDriver.id,
        driverName: selectedDriver.name,
        driverPhone: '+1234567890', // In production, get from driver data
        truckId: selectedDriver.truckNumber,
        truckNumber: selectedDriver.truckNumber,
        pickupLocation: pending.pickup,
        pickupCity: pending.pickup.split(',')[0].trim(),
        pickupState: pending.pickup.split(',')[1]?.trim() || '',
        pickupTime: pending.pickupTime,
        deliveryLocation: pending.delivery,
        deliveryCity: pending.delivery.split(',')[0].trim(),
        deliveryState: pending.delivery.split(',')[1]?.trim() || '',
        deliveryTime: '2024-11-30 14:00', // In production, get from load data
        distance: 900, // In production, calculate from load data
        rate: 2800, // In production, get from load data
        assignedBy: 'AI System',
        notes: `AI recommended with score. Reasoning: ${pending.reasoning}`,
      });
      
      // Add to decision history
      const newDecision: AIDecision = {
        id: Math.random().toString(36).substring(7),
        timestamp: new Date().toLocaleString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit', 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }).replace(',', ''),
        loadNumber: pending.loadNumber,
        dispatchNumber: dispatch.dispatchNumber,
        recommendedDriver: selectedDriver.name,
        approved: true,
        reasoning: pending.reasoning,
        estimatedRevenue: 2800, // In production, get from load data
        alternativesCount: pending.alternativeDrivers.length,
      };
      
      setDecisions([newDecision, ...decisions]);
      setPendingDecisions(pendingDecisions.filter(d => d.id !== pendingId));
      toast.success(`✅ Dispatch ${dispatch.dispatchNumber} created! ${selectedDriver.name} has been notified via SMS.`);
    } catch (error) {
      toast.error('Failed to create dispatch. Please try again.');
      console.error('Dispatch creation error:', error);
    }
  };

  const handleReject = (id: string) => {
    setPendingDecisions(pendingDecisions.filter(d => d.id !== id));
    toast.info('Assignment rejected. You can manually assign this load.');
  };

  const handleSelectDriver = (pendingId: string, driverId: string) => {
    setSelectedDrivers({ ...selectedDrivers, [pendingId]: driverId });
  };

  // If load detail is selected, show detail view
  if (selectedLoad) {
    return <LoadDetailView loadNumber={selectedLoad} onBack={() => setSelectedLoad(null)} />;
  }

  // If dispatch detail is selected, show detail view (for now, redirect to load)
  if (selectedDispatch) {
    // In production, you would have a DispatchDetailView component
    // For now, we'll just show a toast
    toast.info(`Dispatch detail view for ${selectedDispatch} - Coming soon!`);
    setSelectedDispatch(null);
  }

  // If AI is not enabled, show enable prompt
  if (!aiEnabled) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">AI Dispatch Analytics</h2>
              <p className="text-purple-100">
                AI-powered load assignment is currently disabled
              </p>
            </div>
            <div className="bg-white bg-opacity-20 p-4 rounded-lg">
              <Brain className="w-12 h-12" />
            </div>
          </div>
        </div>

        {/* Enable AI Prompt */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-purple-300 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-600 rounded-full mb-4">
              <Power className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Enable AI Dispatch</h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              Turn on AI-powered load assignment to automatically recommend the best drivers for your loads based on location, revenue balance, ELD hours, and workload distribution.
            </p>
            <button
              onClick={handleEnableAI}
              className="inline-flex items-center gap-2 px-8 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-lg shadow-lg"
            >
              <Power className="w-6 h-6" />
              Enable AI Dispatch
            </button>
          </div>

          <div className="p-8 bg-white">
            <h4 className="text-lg font-semibold text-slate-900 mb-4">What you'll get with AI Dispatch:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-slate-900 mb-1">Smart Driver Recommendations</h5>
                  <p className="text-sm text-slate-600">AI analyzes driver location, availability, and workload to suggest the best match</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-slate-900 mb-1">Revenue Balancing</h5>
                  <p className="text-sm text-slate-600">Ensures fair distribution of loads to balance driver earnings</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-slate-900 mb-1">ELD Compliance</h5>
                  <p className="text-sm text-slate-600">Verifies drivers have sufficient hours to complete trips safely</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-semibold text-slate-900 mb-1">Future Location Planning</h5>
                  <p className="text-sm text-slate-600">Considers drivers who will be in the area after completing current loads</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Note</h4>
          <p className="text-sm text-blue-800">
            You can also enable AI Dispatch from the toggle in the header (top right corner). Once enabled, you'll see pending AI recommendations and analytics here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">AI Dispatch Analytics</h2>
            <p className="text-purple-100">
              Track AI performance and decision-making insights
            </p>
          </div>
          <div className="flex items-center gap-4">
            {pendingDecisions.length > 0 && (
              <div className="bg-yellow-500 bg-opacity-20 border border-yellow-300 px-4 py-2 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-300" />
                  <span className="font-semibold">{pendingDecisions.length} Pending</span>
                </div>
              </div>
            )}
            <div className="bg-white bg-opacity-20 p-4 rounded-lg">
              <Brain className="w-12 h-12" />
            </div>
          </div>
        </div>
      </div>

      {/* Pending Decisions */}
      {pendingDecisions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border-2 border-yellow-300">
          <div className="p-4 border-b border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500 p-2 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Pending AI Decisions</h3>
                  <p className="text-sm text-slate-600">Review and approve AI-recommended driver assignments</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-yellow-600">{pendingDecisions.length}</div>
                <div className="text-xs text-slate-500">Awaiting Review</div>
              </div>
            </div>
          </div>

          <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pendingDecisions.map((pending) => (
              <div
                key={pending.id}
                className={`border-2 rounded-xl overflow-hidden ${
                  pending.urgency === 'high'
                    ? 'border-red-300'
                    : pending.urgency === 'medium'
                    ? 'border-yellow-300'
                    : 'border-blue-300'
                }`}
              >
                {/* Header */}
                <div className={`p-3 ${
                  pending.urgency === 'high'
                    ? 'bg-red-100 border-b-2 border-red-300'
                    : pending.urgency === 'medium'
                    ? 'bg-yellow-100 border-b-2 border-yellow-300'
                    : 'bg-blue-100 border-b-2 border-blue-300'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-bold text-slate-900">{pending.loadNumber}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        pending.urgency === 'high'
                          ? 'bg-red-600 text-white'
                          : pending.urgency === 'medium'
                          ? 'bg-yellow-600 text-white'
                          : 'bg-blue-600 text-white'
                      }`}>
                        {pending.urgency.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">{pending.timestamp}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="bg-white bg-opacity-60 rounded p-2">
                      <div className="flex items-center gap-1 text-xs text-slate-600 mb-1">
                        <MapPin className="w-3 h-3" />
                        <span className="font-semibold">Pickup Location</span>
                      </div>
                      <p className="text-sm font-bold text-slate-900">{pending.pickup}</p>
                      <div className="flex items-center gap-1 text-xs text-slate-600 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{pending.pickupTime}</span>
                      </div>
                    </div>
                    <div className="bg-white bg-opacity-60 rounded p-2">
                      <div className="flex items-center gap-1 text-xs text-slate-600 mb-1">
                        <MapPin className="w-3 h-3" />
                        <span className="font-semibold">Delivery Location</span>
                      </div>
                      <p className="text-sm font-bold text-slate-900">{pending.delivery}</p>
                      <div className="flex items-center gap-1 text-xs text-slate-600 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{pending.deliveryTime}</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div className="bg-green-50 border border-green-200 rounded p-2">
                      <p className="text-xs text-green-700 font-semibold">Rate</p>
                      <p className="text-lg font-bold text-green-900">${pending.rate.toLocaleString()}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <p className="text-xs text-blue-700 font-semibold">Total Miles</p>
                      <p className="text-lg font-bold text-blue-900">{pending.totalMiles} mi</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded p-2">
                      <p className="text-xs text-purple-700 font-semibold">Per Mile</p>
                      <p className="text-lg font-bold text-purple-900">${(pending.rate / pending.totalMiles).toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 bg-white">
                  {/* Recommended Driver */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-semibold text-slate-700">🏆 AI Recommendation</h5>
                      <div className="text-right">
                        <span className="text-base font-bold text-purple-600">{pending.recommendedDriver.name}</span>
                        <span className="text-xs text-slate-500 ml-2">({pending.recommendedDriver.truckNumber})</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      <div className="bg-slate-50 rounded p-1.5">
                        <p className="text-[10px] text-slate-500">Distance</p>
                        <p className="text-sm font-bold text-slate-900">{pending.recommendedDriver.distanceToPickup}mi</p>
                      </div>
                      <div className="bg-slate-50 rounded p-1.5">
                        <p className="text-[10px] text-slate-500">Revenue</p>
                        <p className="text-sm font-bold text-slate-900">${pending.recommendedDriver.weeklyRevenue}</p>
                      </div>
                      <div className="bg-slate-50 rounded p-1.5">
                        <p className="text-[10px] text-slate-500">Hours</p>
                        <p className="text-sm font-bold text-slate-900">{pending.recommendedDriver.availableHours}h</p>
                      </div>
                      <div className="bg-slate-50 rounded p-1.5">
                        <p className="text-[10px] text-slate-500">Miles</p>
                        <p className="text-sm font-bold text-slate-900">{pending.recommendedDriver.weeklyMiles}</p>
                      </div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded p-2">
                      <p className="text-[10px] font-semibold text-purple-900 mb-1">🧠 AI REASONING</p>
                      <p className="text-xs text-purple-800 leading-tight">{pending.reasoning}</p>
                    </div>
                  </div>

                  {/* Alternative Drivers */}
                  {pending.alternativeDrivers.length > 0 && (
                    <div className="mb-3">
                      <button
                        onClick={() => setExpandedOptions(prev => ({ ...prev, [pending.id]: !prev[pending.id] }))}
                        className="w-full flex items-center justify-between p-2 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors mb-2"
                      >
                        <h5 className="text-sm font-semibold text-slate-700">📋 All Options ({pending.alternativeDrivers.length + 1})</h5>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={`transition-transform ${expandedOptions[pending.id] ? 'rotate-180' : ''}`}
                        >
                          <path d="m6 9 6 6 6-6"/>
                        </svg>
                      </button>
                      {expandedOptions[pending.id] && (
                        <div className="space-y-1.5">
                        {/* AI Recommended Driver as first option */}
                        <div
                          onClick={() => handleSelectDriver(pending.id, pending.recommendedDriver.id)}
                          className={`border rounded-lg p-2 cursor-pointer transition-all ${
                            !selectedDrivers[pending.id] || selectedDrivers[pending.id] === pending.recommendedDriver.id
                              ? 'border-green-500 bg-green-50'
                              : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={!selectedDrivers[pending.id] || selectedDrivers[pending.id] === pending.recommendedDriver.id}
                                onChange={() => handleSelectDriver(pending.id, pending.recommendedDriver.id)}
                                className="w-3 h-3 text-green-600"
                              />
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-semibold text-slate-900">{pending.recommendedDriver.name}</span>
                                <span className="text-xs text-slate-500">({pending.recommendedDriver.truckNumber})</span>
                                <span className="px-1.5 py-0.5 bg-green-600 text-white text-[9px] font-bold rounded">AI PICK</span>
                              </div>
                            </div>
                            <span className="text-xs font-bold text-green-600">Score: 100</span>
                          </div>
                          <div className="grid grid-cols-4 gap-1 mb-1 ml-5">
                            <div>
                              <p className="text-[9px] text-slate-500">Dist</p>
                              <p className="text-xs font-bold text-slate-900">{pending.recommendedDriver.distanceToPickup}mi</p>
                            </div>
                            <div>
                              <p className="text-[9px] text-slate-500">Rev</p>
                              <p className="text-xs font-bold text-slate-900">${pending.recommendedDriver.weeklyRevenue}</p>
                            </div>
                            <div>
                              <p className="text-[9px] text-slate-500">Hrs</p>
                              <p className="text-xs font-bold text-slate-900">{pending.recommendedDriver.availableHours}h</p>
                            </div>
                            <div>
                              <p className="text-[9px] text-slate-500">Miles</p>
                              <p className="text-xs font-bold text-slate-900">{pending.recommendedDriver.weeklyMiles}</p>
                            </div>
                          </div>
                          <p className="text-[10px] text-slate-600 ml-5 leading-tight">{pending.reasoning}</p>
                        </div>

                        {/* Alternative Drivers */}
                        {pending.alternativeDrivers.map((alt) => {
                          const isSelected = selectedDrivers[pending.id] === alt.driver.id;
                          return (
                            <div
                              key={alt.driver.id}
                              onClick={() => handleSelectDriver(pending.id, alt.driver.id)}
                              className={`border rounded-lg p-2 cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    checked={isSelected}
                                    onChange={() => handleSelectDriver(pending.id, alt.driver.id)}
                                    className="w-3 h-3 text-blue-600"
                                  />
                                  <div>
                                    <span className="text-sm font-semibold text-slate-900">{alt.driver.name}</span>
                                    <span className="text-xs text-slate-500 ml-1.5">({alt.driver.truckNumber})</span>
                                  </div>
                                </div>
                                <span className="text-xs font-bold text-blue-600">Score: {alt.score}</span>
                              </div>
                              <div className="grid grid-cols-4 gap-1 mb-1 ml-5">
                                <div>
                                  <p className="text-[9px] text-slate-500">Dist</p>
                                  <p className="text-xs font-bold text-slate-900">{alt.driver.distanceToPickup}mi</p>
                                </div>
                                <div>
                                  <p className="text-[9px] text-slate-500">Rev</p>
                                  <p className="text-xs font-bold text-slate-900">${alt.driver.weeklyRevenue}</p>
                                </div>
                                <div>
                                  <p className="text-[9px] text-slate-500">Hrs</p>
                                  <p className="text-xs font-bold text-slate-900">{alt.driver.availableHours}h</p>
                                </div>
                                <div>
                                  <p className="text-[9px] text-slate-500">Miles</p>
                                  <p className="text-xs font-bold text-slate-900">{alt.driver.weeklyMiles}</p>
                                </div>
                              </div>
                              <p className="text-[10px] text-slate-600 ml-5 leading-tight">{alt.reason}</p>
                            </div>
                          );
                        })}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleReject(pending.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-semibold"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </button>
                    <button
                      onClick={() => handleApprove(pending.id, selectedDrivers[pending.id])}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {selectedDrivers[pending.id] ? 'Approve Selected' : 'Approve AI'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-2xl font-bold text-slate-900">{stats.totalDecisions}</span>
          </div>
          <p className="text-sm text-slate-600">Total AI Decisions</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-2xl font-bold text-green-600">{stats.approvalRate}%</span>
          </div>
          <p className="text-sm text-slate-600">Approval Rate</p>
          <p className="text-xs text-slate-500 mt-1">{stats.approved} approved, {stats.rejected} rejected</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-purple-100 p-2 rounded-lg">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-2xl font-bold text-slate-900">${stats.averageRevenue}</span>
          </div>
          <p className="text-sm text-slate-600">Avg Revenue per Load</p>
          <p className="text-xs text-slate-500 mt-1">{stats.revenueAccuracy}% prediction accuracy</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="bg-orange-100 p-2 rounded-lg">
              <Award className="w-5 h-5 text-orange-600" />
            </div>
            <span className="text-2xl font-bold text-slate-900">{stats.onTimeDeliveries}</span>
          </div>
          <p className="text-sm text-slate-600">On-Time Deliveries</p>
          <p className="text-xs text-slate-500 mt-1">From approved assignments</p>
        </div>
      </div>

      {/* Decision History */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Decision History</h3>
          <p className="text-sm text-slate-600">Review all AI assignment recommendations and outcomes</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Timestamp</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Load</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Dispatch</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Recommended Driver</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Revenue</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Delivery</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">Alternatives</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {decisions.map((decision) => (
                <tr key={decision.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-600">{decision.timestamp}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setSelectedLoad(decision.loadNumber)}
                      className="font-semibold text-[#AC021A] hover:text-[#8A0115] hover:underline transition-colors"
                    >
                      {decision.loadNumber}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    {decision.dispatchNumber ? (
                      <button
                        onClick={() => setSelectedDispatch(decision.dispatchNumber!)}
                        className="font-semibold text-[#AC021A] hover:text-[#8A0115] hover:underline transition-colors"
                      >
                        {decision.dispatchNumber}
                      </button>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-900">{decision.recommendedDriver}</td>
                  <td className="px-6 py-4">
                    {decision.approved ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3" />
                        Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                        <XCircle className="w-3 h-3" />
                        Rejected
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {decision.actualRevenue ? (
                      <div>
                        <p className="text-sm font-semibold text-slate-900">${decision.actualRevenue}</p>
                        <p className="text-xs text-slate-500">Est: ${decision.estimatedRevenue}</p>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Pending</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {decision.deliveryOnTime === true ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                        <Clock className="w-3 h-3" />
                        On Time
                      </span>
                    ) : decision.deliveryOnTime === false ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                        <Clock className="w-3 h-3" />
                        Late
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">In Progress</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{decision.alternativesCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Reasoning */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent AI Reasoning</h3>
        <div className="space-y-3">
          {decisions.slice(0, 3).map((decision) => (
            <div key={decision.id} className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="font-semibold text-slate-900">{decision.loadNumber}</span>
                  <span className="text-slate-400 mx-2">→</span>
                  <span className="text-slate-700">{decision.recommendedDriver}</span>
                </div>
                <span className="text-xs text-slate-500">{decision.timestamp}</span>
              </div>
              <p className="text-sm text-slate-600">{decision.reasoning}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
